import { createContext, useContext, useState, useCallback, useMemo, forwardRef, type ReactNode } from "react";

// Lazy-import trackEvent to avoid pulling supabase SDK (173KB) into the critical bundle
const lazyTrackEvent = (eventName: string, params?: Record<string, any>) => {
  import("@/lib/fbPixel").then(({ trackEvent }) => trackEvent(eventName, params)).catch(() => {});
};

export interface CartItem {
  productId: number | string;
  name: string;
  price: string;
  size: string;
  image: string;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (item: Omit<CartItem, "quantity">, quantity?: number, openDrawer?: boolean) => void;
  removeItem: (productId: number | string, size: string) => void;
  updateQuantity: (productId: number | string, size: string, quantity: number) => void;
  changeItemSize: (productId: number | string, oldSize: string, newSize: string) => void;
  clearCart: () => void;
  totalItems: number;
  totalAmount: number;
}

const CartContext = createContext<CartContextType | null>(null);

const defaultCart: CartContextType = {
  items: [], isOpen: false, openCart: () => {}, closeCart: () => {},
  addItem: () => {}, removeItem: () => {}, updateQuantity: () => {},
  changeItemSize: () => {}, clearCart: () => {}, totalItems: 0, totalAmount: 0,
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  return ctx ?? defaultCart;
};

const parsePrice = (price: string): number =>
  Number(price.replace(/[^0-9.]/g, "")) || 0;

const CART_STORAGE_KEY = "kenani_cart_items";

const readStoredCart = (): CartItem[] => {
  try {
    const raw = sessionStorage.getItem(CART_STORAGE_KEY) || localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((item) => item?.productId && item?.name && item?.size) : [];
  } catch {
    return [];
  }
};

const writeStoredCart = (items: CartItem[]) => {
  try {
    const payload = JSON.stringify(items);
    sessionStorage.setItem(CART_STORAGE_KEY, payload);
    localStorage.setItem(CART_STORAGE_KEY, payload);
  } catch {}
};

export const CartProvider = forwardRef<HTMLDivElement, { children: ReactNode }>(({ children }, _ref) => {
  const [items, setItems] = useState<CartItem[]>(() => readStoredCart());
  const [isOpen, setIsOpen] = useState(false);

  const openCart = useCallback(() => {
    setIsOpen(true);
    // Fire view_cart whenever drawer opens
    setItems((current) => {
      const value = current.reduce((sum, i) => sum + parsePrice(i.price) * i.quantity, 0);
      lazyTrackEvent("view_cart", {
        currency: "BDT",
        value,
        items: current.map((i) => ({
          item_id: String(i.productId),
          item_name: i.name,
          item_variant: i.size,
          price: parsePrice(i.price),
          quantity: i.quantity,
        })),
      });
      return current;
    });
  }, []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const addItem = useCallback((item: Omit<CartItem, "quantity">, quantity: number = 1, openDrawer: boolean = true) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.productId === item.productId && i.size === item.size);
      if (existing) {
        const next = prev.map((i) =>
          i.productId === item.productId && i.size === item.size
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
        writeStoredCart(next);
        return next;
      }
      const next = [...prev, { ...item, quantity }];
      writeStoredCart(next);
      return next;
    });
    if (openDrawer) setIsOpen(true);
    const unitPrice = parsePrice(item.price);
    lazyTrackEvent("add_to_cart", {
      currency: "BDT",
      value: unitPrice * quantity,
      items: [{
        item_id: String(item.productId),
        item_name: item.name,
        item_variant: item.size,
        price: unitPrice,
        quantity,
      }],
    });
  }, []);

  const removeItem = useCallback((productId: number | string, size: string) => {
    setItems((prev) => {
      const removed = prev.find((i) => i.productId === productId && i.size === size);
      if (removed) {
        const unitPrice = parsePrice(removed.price);
        lazyTrackEvent("remove_from_cart", {
          currency: "BDT",
          value: unitPrice * removed.quantity,
          items: [{
            item_id: String(removed.productId),
            item_name: removed.name,
            item_variant: removed.size,
            price: unitPrice,
            quantity: removed.quantity,
          }],
        });
      }
      const next = prev.filter((i) => !(i.productId === productId && i.size === size));
      writeStoredCart(next);
      return next;
    });
  }, []);

  const updateQuantity = useCallback((productId: number | string, size: string, quantity: number) => {
    if (quantity < 1) return;
    setItems((prev) => {
      const next = prev.map((i) =>
        i.productId === productId && i.size === size ? { ...i, quantity } : i
      );
      writeStoredCart(next);
      return next;
    });
  }, []);

  const changeItemSize = useCallback((productId: number | string, oldSize: string, newSize: string) => {
    if (!newSize || oldSize === newSize) return;
    setItems((prev) => {
      const target = prev.find((i) => i.productId === productId && i.size === oldSize);
      if (!target) return prev;
      const existing = prev.find((i) => i.productId === productId && i.size === newSize);
      let next: CartItem[];
      if (existing) {
        next = prev
          .filter((i) => !(i.productId === productId && i.size === oldSize))
          .map((i) =>
            i.productId === productId && i.size === newSize
              ? { ...i, quantity: i.quantity + target.quantity }
              : i
          );
      } else {
        next = prev.map((i) =>
          i.productId === productId && i.size === oldSize ? { ...i, size: newSize } : i
        );
      }
      writeStoredCart(next);
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    writeStoredCart([]);
    setItems([]);
  }, []);

  const totalItems = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  const totalAmount = useMemo(() => items.reduce((sum, i) => sum + parsePrice(i.price) * i.quantity, 0), [items]);

  const value = useMemo<CartContextType>(() => ({
    items, isOpen, openCart, closeCart, addItem, removeItem, updateQuantity, changeItemSize, clearCart, totalItems, totalAmount,
  }), [items, isOpen, openCart, closeCart, addItem, removeItem, updateQuantity, changeItemSize, clearCart, totalItems, totalAmount]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
});

CartProvider.displayName = "CartProvider";
